/**
 * Manager approval for a manual discount over the cashier's limit (DISCOUNT_V2_PLAN.md §9.12, R2.4d.2):
 * the limit, whether an approval already covers the bill, the record a sale's approval moves to, and what
 * a press must do about it. Pure. Rewritten from rista-pos-common's `discountLimitCheck`,
 * `getUpdatedDiscountApprovalData` and `getCurrentDiscountApprovalData` (§9.12's rista-pos-common rule),
 * held to them by `approval.test.ts`, with one difference decided by popup:
 *
 * 🔴 **Coverage is the backend's (decision 44).** An approval covers the bill while the bill's MANUAL
 * discount is no more than the amount an approved manual-discount request covered
 * (`saletxn_service.js` 1806-1812, the check the save itself runs). v1 compared that amount with the
 * bill's WHOLE discount, so an auto deal added after the approval asked the manager again.
 *
 * Where the record lives (decision 45): in v1's `discountStore.discountApproval` until v1's approval
 * screens are cut over, read and written only through `../v1Slots.ts`; these rules take and return
 * records and never touch a store.
 */
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";

import type { Sale } from "../types";

/** The backend's stages for an approval request (`DISCOUNT_APPROVAL_STAGE` in v1; rule 14: v2 declares its own). */
export const APPROVAL_STAGE = { approved: "Approved", requested: "Requested", rejected: "Rejected" } as const;

/** The request type the backend records a manual-discount approval under (`saletxn_service.js` 1809). */
export const MANUAL_DISCOUNT_REQUEST = "MANUAL_DISCOUNT";

/** A closed sale's discount can only be changed within the limit (v1's `REST_ERR_DISCOUNT_LIMIT_EXCEEDED` on a closed sale). */
const CLOSED_SALE = "Closed";

/**
 * A sale's manager-approval record: whether its discount needs approval, and where the request stands.
 * v1 keeps the same facts as rista-pos-common's `DiscountApprovalType`; `../v1Slots.ts` converts.
 */
export interface ApprovalRecord {
  /** The sale it is about (its `mid`). */
  saleId: string;
  /** The bill's manual discount is over the cashier's limit and not yet approved (v1 `isApprovalRequired`). */
  required: boolean;
  /** A request was sent and is waiting (v1 `isRequested`). */
  requested: boolean;
  approved: boolean;
  /** The request the server answered with (v1 `approvalRequestId`). */
  requestId?: string;
  /** The server's stage for the request (v1 `currentStatus`): one of `APPROVAL_STAGE`. */
  status?: string;
  /** The manual discount the record is about; a larger one needs a new approval. */
  totalDiscount?: number;
  /** The status answer as the server sent it (v1 `approvalData`): read by the approval wait's view. */
  answer?: Record<string, unknown>;
}

/** What the cashier's limit says about the bill's manual discount. */
export interface LimitCheck {
  /** The sale is closed and its manual discount is over the limit: the override is simply refused. */
  refused: boolean;
  /** The manual discount is over the limit and no approval covers it (v1's `shouldRender`). */
  needsApproval: boolean;
  /** The manual discount the check is about; 0 when it is within the limit. */
  manualDiscountTotal: number;
}

/**
 * Does an approved manual-discount request on the sale cover this much manual discount (decision 44)?
 * The sale's `approvalRequests` are the server's record, so a request another terminal sent counts too.
 */
export function approvalCovers(sale: Sale, manualDiscountTotal: number): boolean {
  const requests: any[] = (sale as any)?.approvalRequests ?? [];
  return requests.some(
    (request) =>
      request?.requestType === MANUAL_DISCOUNT_REQUEST &&
      request?.status === APPROVAL_STAGE.approved &&
      Number(request?.data?.manualDiscountTotal) >= manualDiscountTotal,
  );
}

/**
 * The bill's manual discount against the cashier's limit (`discountPercentLimit`, the capability facts'
 * `subject.limits`). The arithmetic is rista-library's `validateManualDiscountLimit`, the same function
 * the backend runs at the save, so the two cannot disagree about the total.
 */
export function checkManualDiscountLimit(sale: Sale, discountPercentLimit: number | null | undefined): LimitCheck {
  const overLimit: { code?: string; manualDiscountTotal?: number } | undefined = saleutil.validateManualDiscountLimit(sale, {
    discountPercentLimit,
  });
  if (!overLimit?.code) return { refused: false, needsApproval: false, manualDiscountTotal: 0 };

  const manualDiscountTotal = overLimit.manualDiscountTotal ?? 0;
  if ((sale as any)?.saleStatus === CLOSED_SALE) return { refused: true, needsApproval: false, manualDiscountTotal: 0 };
  return { refused: false, needsApproval: !approvalCovers(sale, manualDiscountTotal), manualDiscountTotal };
}

/**
 * The record a sale's approval moves to after a limit check (rista-pos-common `getUpdatedDiscountApprovalData`).
 *
 * ⚠️ Kept as v1 (decision 12): only the sale's own record counts, so a pending request whose amount did
 * not grow is still marked "approval required" again, and a Save may send a second request.
 */
export function nextApprovalRecord(saleId: string, check: LimitCheck, current: ApprovalRecord | undefined): ApprovalRecord {
  const cleared: ApprovalRecord = { saleId, required: false, requested: false, approved: false };
  if (!check.needsApproval) return cleared;

  const coversMore = (current?.totalDiscount ?? 0) >= check.manualDiscountTotal;
  const approved = current?.status === APPROVAL_STAGE.approved;
  if (coversMore && approved) return { ...cleared, totalDiscount: current?.totalDiscount };
  return { ...cleared, required: true, totalDiscount: check.manualDiscountTotal };
}

/** What a press (Save, Checkout) must do about approval before it may go on. */
export type ApprovalDecision =
  /** Nothing to ask. */
  | { kind: "clear"; record: ApprovalRecord; manualDiscountTotal: number }
  /** The manager must approve first; `autoRequest` when the host sends the request itself (Table View, My Orders). */
  | { kind: "approval-required"; record: ApprovalRecord; manualDiscountTotal: number; autoRequest: boolean }
  /** A closed sale's discount over the limit: refused, nothing to approve. */
  | { kind: "refused"; record: ApprovalRecord };

export function decideApproval({
  sale,
  discountPercentLimit,
  current,
  autoRequest,
}: {
  sale: Sale;
  discountPercentLimit: number | null | undefined;
  /** The sale's record as it stands (`v1Slots.readApprovalRecord`, decision 45). */
  current: ApprovalRecord | undefined;
  autoRequest: boolean;
}): ApprovalDecision {
  const check = checkManualDiscountLimit(sale, discountPercentLimit);
  const record = nextApprovalRecord(saleIdOf(sale), check, current);
  if (check.refused) return { kind: "refused", record };
  if (check.needsApproval && record.required) {
    return { kind: "approval-required", record, manualDiscountTotal: check.manualDiscountTotal, autoRequest };
  }
  return { kind: "clear", record, manualDiscountTotal: check.manualDiscountTotal };
}

/**
 * After a repricing LOWERED the bill's discount: the record to reset to, or null when approval would
 * still be asked (v1's `checkAndUpdateApprovalState`: "only update here, if we are trying to reset").
 * The shared write path (`ordering/writePath`) asks it.
 */
export function resetAfterRepricing(
  sale: Sale,
  discountPercentLimit: number | null | undefined,
  current: ApprovalRecord | undefined,
): ApprovalRecord | null {
  const check = checkManualDiscountLimit(sale, discountPercentLimit);
  if (check.needsApproval) return null;
  return nextApprovalRecord(saleIdOf(sale), check, current);
}

/**
 * The record a save answered with when it asked a manager: the answer's last approval request, waiting
 * (web v1 `DiscountDeals` › the callbacks of `saveToCart` and `sendRequest`, mobile's `saveToCart`, which
 * each build it inline). `undefined` when the save asked nobody.
 */
export function requestedApprovalRecord(answer: Record<string, any> | null | undefined): ApprovalRecord | undefined {
  if (!answer?.approvalRequestId) return undefined;
  const requests: Record<string, any>[] = Array.isArray(answer.approvalRequests) ? answer.approvalRequests : [];
  const last = requests[requests.length - 1] ?? {};
  return {
    saleId: String(answer.mid ?? ""),
    required: false,
    requested: true,
    approved: false,
    ...(last.requestId ? { requestId: String(last.requestId) } : {}),
    ...(last.status ? { status: String(last.status) } : {}),
    ...(answer.totalDiscount !== undefined ? { totalDiscount: Number(answer.totalDiscount) } : {}),
    ...(Object.keys(last).length > 0 ? { answer: last } : {}),
  };
}

const saleIdOf = (sale: Sale): string => String((sale as any)?.mid ?? "");
